import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Search, X, Check, Wand2, ShieldOff, User, RefreshCw, ClipboardCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";
const STATUS_META = {
  dirty:       { label: "Dirty",       color: "bg-amber-50 text-amber-700 border-amber-200",   dot: "bg-amber-500", icon: Wand2 },
  in_progress: { label: "In progress", color: "bg-sky-50 text-sky-700 border-sky-200",         dot: "bg-sky-500",   icon: RefreshCw },
  clean:       { label: "Clean",       color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", icon: Sparkles },
  inspected:   { label: "Inspected",   color: "bg-slate-100 text-slate-600 border-slate-200",  dot: "bg-slate-400", icon: ClipboardCheck },
  blocked:     { label: "Blocked",     color: "bg-slate-900/5 text-slate-700 border-slate-300", dot: "bg-slate-700", icon: ShieldOff },
};
const COLS = ["dirty", "in_progress", "clean", "inspected", "blocked"];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 60 / 24)}d ago`;
};

export default function HousekeepingTab({ pid, refreshKey }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [openUnit, setOpenUnit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: d } = await api.get(`/pms/properties/${pid}/housekeeping`);
      setData(d);
      // If the drawer is open, refresh its row so the checklist/status stays in sync
      setOpenUnit((cur) => cur ? d.units.find((u) => u.unit_id === cur.unit_id) || cur : cur);
    } catch (e) { toast.error(errMsg(e)); }
  }, [pid]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (uid, status, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/pms/properties/${pid}/housekeeping/${uid}/status`, { status, notes: "", assigned_to: "", ...extra });
      toast.success(`Unit → ${STATUS_META[status].label}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const toggleItem = async (uid, item) => {
    // Optimistic update
    setData((d) => {
      if (!d) return d;
      return { ...d, units: d.units.map((u) => u.unit_id === uid && u.task ? {
        ...u,
        task: { ...u.task, checklist: u.task.checklist.map((c) => c.id === item.id ? { ...c, done: !item.done } : c) },
      } : u) };
    });
    try {
      await api.post(`/pms/properties/${pid}/housekeeping/${uid}/checklist`, { item_id: item.id, done: !item.done });
      await load();
    } catch (e) { toast.error(errMsg(e)); await load(); }
  };

  const flagDirty = async (uid) => {
    setBusy(true);
    try {
      await api.post(`/pms/properties/${pid}/housekeeping/tasks`, { unit_id: uid, notes: "" });
      toast.success("Unit flagged Dirty");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (!data) return <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-slate-300" size={26} /></div>;

  const filtered = data.units.filter((u) => !q.trim() || u.unit_name.toLowerCase().includes(q.toLowerCase()));
  const groups = COLS.reduce((acc, s) => ({ ...acc, [s]: filtered.filter((u) => u.status === s) }), {});

  return (
    <div className="reveal">
      {/* Top strip: search + counts */}
      <div className="flex items-center gap-3 flex-wrap mb-5">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="hk-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter unit…"
            className="pl-8 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 w-56" />
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {COLS.map((s) => {
            const m = STATUS_META[s];
            return (
              <div key={s} data-testid={`hk-count-${s}`} className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border ${m.color} flex items-center gap-1.5`}>
                <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label} <span className="opacity-70">· {data.counts[s] || 0}</span>
              </div>
            );
          })}
          <button data-testid="hk-refresh" onClick={load} className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 flex items-center gap-1.5 text-slate-600">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>

      {/* Kanban board — 1 col on phone, 2 on tablet portrait, 3 on landscape, 5 on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
        {COLS.map((s) => {
          const m = STATUS_META[s];
          const items = groups[s];
          const Icon = m.icon;
          return (
            <div key={s} data-testid={`hk-col-${s}`} className="bg-slate-50/60 rounded-2xl border border-slate-200 flex flex-col min-h-[400px]">
              <div className={`px-4 py-3 border-b border-slate-200 flex items-center gap-2 ${m.color} rounded-t-2xl`}>
                <Icon size={14} />
                <h4 className="font-display font-bold text-sm">{m.label}</h4>
                <span className="ml-auto text-[11px] font-mono opacity-70">{items.length}</span>
              </div>
              <div className="p-3 space-y-2 flex-1 overflow-y-auto max-h-[70vh]">
                {items.length === 0 && (
                  <p className="text-[11px] text-slate-400 italic text-center py-6">No units here.</p>
                )}
                {items.map((u) => {
                  const t = u.task;
                  const done = t ? t.checklist.filter((c) => c.done).length : 0;
                  const total = t ? t.checklist.length : 0;
                  const pct = total ? Math.round((done / total) * 100) : (s === "clean" ? 100 : 0);
                  return (
                    <button key={u.unit_id} data-testid={`hk-card-${u.unit_id}`} onClick={() => setOpenUnit(u)}
                      className="w-full text-left bg-white rounded-xl border border-slate-200 hover:border-[#4F46E5] hover:shadow-sm transition-all p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-slate-900 text-sm">Unit {u.unit_name}</span>
                        {t?.source === "departure" && <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Departure</span>}
                        {t?.source === "manual" && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full">Manual</span>}
                        {t?.source === "block" && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-200 px-1.5 py-0.5 rounded-full">Block</span>}
                      </div>
                      {t?.guest && <p className="text-[11px] text-slate-500 mt-1 truncate">Prev guest: {t.guest}</p>}
                      {t?.assigned_to && <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1"><User size={9} /> {t.assigned_to}</p>}
                      {total > 0 && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <p className="text-[10px] font-mono text-slate-400 mt-1">{done}/{total} · {pct}%</p>
                        </div>
                      )}
                      {s === "clean" && !t && <p className="text-[10px] text-slate-400 italic mt-1">Ready for the next guest.</p>}
                      {t && <p className="text-[9px] text-slate-400 mt-1.5 font-mono">Opened {timeAgo(t.created_at)}</p>}
                    </button>
                  );
                })}
              </div>
              {s === "clean" && (
                <div className="p-3 border-t border-slate-200">
                  <p className="text-[10px] text-slate-500 mb-2 leading-snug">Spilled coffee? Ad-hoc clean? Flag any unit dirty:</p>
                  <UnitDirtyDropdown units={data.units} onFlag={flagDirty} disabled={busy} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Drawer */}
      {openUnit && <HkDrawer u={openUnit} onClose={() => setOpenUnit(null)} onStatus={setStatus} onToggle={toggleItem} busy={busy} />}

      <p className="text-[11px] text-slate-400 mt-5">Departures automatically become <span className="font-semibold text-amber-600">Dirty</span>. Ticking every checklist item advances a task to <span className="font-semibold text-emerald-600">Clean</span>. Front-office marks Inspected once a supervisor confirms.</p>
    </div>
  );
}

const UnitDirtyDropdown = ({ units, onFlag, disabled }) => {
  const [uid, setUid] = useState("");
  const eligible = units.filter((u) => u.status === "clean");
  return (
    <div className="flex gap-1.5">
      <select data-testid="hk-manual-unit" value={uid} onChange={(e) => setUid(e.target.value)} className="flex-1 text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none">
        <option value="">Pick a unit…</option>
        {eligible.map((u) => <option key={u.unit_id} value={u.unit_id}>Unit {u.unit_name}</option>)}
      </select>
      <button data-testid="hk-manual-flag" onClick={() => { if (uid) { onFlag(uid); setUid(""); } }} disabled={!uid || disabled}
        className="btn-brand rounded-lg px-3 py-1.5 text-[11px] font-semibold disabled:opacity-40">Flag</button>
    </div>
  );
};

const HkDrawer = ({ u, onClose, onStatus, onToggle, busy }) => {
  const t = u.task;
  return (
    <Portal>
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-3 sm:p-4 md:p-6 overflow-y-auto pt-safe pb-safe pl-safe pr-safe" onClick={onClose}>
      <div data-testid="hk-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-5 md:p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><Sparkles size={16} className="brand-pink" /> Unit {u.unit_name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">Housekeeping · <span className="capitalize">{u.status.replace("_", " ")}</span></p>
          </div>
          <button data-testid="hk-drawer-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {!t && (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
            This unit is <span className="font-bold">Clean</span> and ready. To flag it dirty, use the Clean column tools on the board.
          </div>
        )}

        {t && (
          <>
            <div className="mt-6 grid grid-cols-2 gap-2 text-[11px]">
              <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                <p className="text-slate-400 uppercase font-semibold tracking-wider">Source</p>
                <p className="text-slate-800 font-semibold capitalize">{t.source}</p>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                <p className="text-slate-400 uppercase font-semibold tracking-wider">Opened</p>
                <p className="text-slate-800 font-semibold">{timeAgo(t.created_at)}</p>
              </div>
              {t.guest && (
                <div className="col-span-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                  <p className="text-slate-400 uppercase font-semibold tracking-wider">Previous guest</p>
                  <p className="text-slate-800 font-semibold">{t.guest}</p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-2">Cleaning checklist</p>
              <div className="space-y-2">
                {t.checklist.map((item) => (
                  <label key={item.id} data-testid={`hk-check-${item.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${item.done ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-400"}`}>
                    <button onClick={(e) => { e.preventDefault(); onToggle(u.unit_id, item); }}
                      className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${item.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300"}`}>
                      {item.done && <Check size={12} />}
                    </button>
                    <span className={`text-sm ${item.done ? "text-emerald-800 line-through" : "text-slate-700"}`}>{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Status transitions</p>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="hk-status-dirty" onClick={() => onStatus(u.unit_id, "dirty")} disabled={busy || u.status === "dirty"}
                  className="rounded-full py-2 text-[11px] font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-40">Mark Dirty</button>
                <button data-testid="hk-status-progress" onClick={() => onStatus(u.unit_id, "in_progress")} disabled={busy || u.status === "in_progress"}
                  className="rounded-full py-2 text-[11px] font-semibold border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-40">Start cleaning</button>
                <button data-testid="hk-status-clean" onClick={() => onStatus(u.unit_id, "clean")} disabled={busy || u.status === "clean"}
                  className="rounded-full py-2 text-[11px] font-semibold border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">Mark Clean</button>
                <button data-testid="hk-status-inspected" onClick={() => onStatus(u.unit_id, "inspected")} disabled={busy || u.status !== "clean"}
                  className="rounded-full py-2 text-[11px] font-semibold border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-40">Inspected · Close</button>
              </div>
              <p className="text-[10px] text-slate-400 leading-tight pt-1">Inspected closes the task for today and returns the unit to the Clean pool. Front Office privileges: <span className="font-mono">Mark Dirty / Mark Clean</span>.</p>
            </div>
          </>
        )}
      </div>
    </div>
    </Portal>
  );
};
